import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { MENU_ROUTES } from 'App/components/sidebar/constants';
import { logUserOut, useIsLoggedIn } from 'App/services/idpTokenService';
import AccountHero from 'Pages/help/components/accountSidebar/accountHero/AccountHero';
import MenuLink from 'Pages/help/components/accountSidebar/menuLink/MenuLink';
import {
  Copyright,
  DataPoint,
  Divider,
  GDPRLink,
  LogoContainer,
  LogoImage,
  LogoText,
  LogOutButton,
  MenuContainer,
  MenuTitle,
  SidebarContainer,
  UserData,
  UserDataContainer,
} from 'Pages/help/components/accountSidebar/style';
import { ACCOUNT_SIDEBAR_TEST_ID, LOGO_TEST_ID } from 'Pages/help/testId';
import Logo from 'Shared/assets/images/logo.png';
import { Button, ButtonWidth, ContextTypes } from 'Shared/components/design/button/Button';
import { LEADERBOARD_SIGNUP_DURATION_DAYS } from 'Shared/components/design/leaderBoard/service/leaderBoardService';
import {ICON_IDS} from "Shared/components/icons/constants";
import useGpHasEverCompleted from 'Shared/hooks/useGpHasEverCompleted';
import useMembershipDuration from 'Shared/hooks/useMembershipDuration';
import { useUserPointsFromACT } from 'Shared/services/act/app/actService';
import { ANALYTICS_EVENT_ATTRIBUTES, ANALYTICS_EVENT_SOURCE } from 'Shared/services/analytics/constants';
import { analyticsTrackReferralBonusStart } from 'Shared/services/analytics/events/referralBonusStart';
import { useGetUserInfo, useGetUserInfoWithInviteId } from 'Shared/services/userActivation/api/idpApiService';
import { capitalize } from 'Shared/utils/formatUtils';

/**
 * Renders the User Activation component
 */
function AccountSidebar(): JSX.Element {
  const { t }                                   = useTranslation('accountHelp');
  const isLoggedOut                             = !useIsLoggedIn();
  const isMenuPersonalInfoActive                = window.location.pathname.includes(MENU_ROUTES.ACCOUNT_PERSONAL_INFO);
  const isMenuContactUsActive                   = window.location.pathname.includes(MENU_ROUTES.ACCOUNT_CONTACT_US);
  const isMenuGetSupportActive                  = window.location.pathname.includes(MENU_ROUTES.ACCOUNT_SUPPORT);
  const isMenuTermsActive                       = window.location.pathname.includes(MENU_ROUTES.ACCOUNT_TERMS);
  const isMenuPrivacyActive                     = window.location.pathname.includes(MENU_ROUTES.ACCOUNT_PRIVACY);
  const isMenuInviteActive                      = window.location.pathname.includes(MENU_ROUTES.ACCOUNT_INVITE);
  const isMenuProfileQuestionsActive            = window.location.pathname.includes(MENU_ROUTES.ACCOUNT_PROFILE_QUESTIONS);
  const isMenuLeaderboardActive                 = window.location.pathname.includes(MENU_ROUTES.ACCOUNT_LEADERBOARD);
  const currentYear                             = new Date().getFullYear();
  const DAYS_PER_MONTH                          = 30;
  const DAYS_PER_YEAR                           = 365;
  const IS_SETTINGS_DISABLED                    = true;
  const { data: userInfoSWR }                   = useGetUserInfo(true);
  const { data: userPointsFromACTSWR }          = useUserPointsFromACT(true);
  const gpCompleted                             = useGpHasEverCompleted(!isLoggedOut);
  const { data: userInfo }                      = useGetUserInfoWithInviteId();
  const userEmail                               = userInfoSWR?.email || '';
  const userLifetimePoints                      = userPointsFromACTSWR?.lifetimePoints || '';
  const userName                                = userInfoSWR ? (capitalize(userInfoSWR.firstName || '') + ' ' + capitalize(userInfoSWR.lastName || '')) : '';
  const { membershipLabel, membershipDuration } = (memberSince => {
    const currentDate = new Date();
    const signUpDate  = memberSince ?? currentDate;
    const years       = currentDate.getFullYear() - signUpDate.getFullYear();
    const months      = (currentDate.getMonth() - signUpDate.getMonth()) + 12 * years;
    const days        = (currentDate.getTime() - signUpDate.getTime()) / (1000 * 3600 * 24);

    if (days < DAYS_PER_MONTH) {
      return {
        membershipLabel : t('AccountSidebar--userAvatar-data-membershipDays'),
        membershipDuration : Math.round(days),
      };
    } else if (days > DAYS_PER_MONTH && days < DAYS_PER_YEAR) {
      return {
        membershipLabel : t('AccountSidebar--userAvatar-data-membershipMonths'),
        membershipDuration : Math.round(months),
      };
    } else {
      return {
        membershipLabel : t('AccountSidebar--userAvatar-data-membershipYears'),
        membershipDuration : Math.abs(years),
      };
    }
  })(userInfoSWR?.createdAt ?? new Date());

  const isLoggedInContentAvailable = !isLoggedOut && userName && userEmail;
  const isLeaderBoardAvailable     = useMembershipDuration(LEADERBOARD_SIGNUP_DURATION_DAYS);

  /**
   * Track a link visit event for referral bonuses in analytics.
   *
   * This function calls `analyticsTrackReferralBonusStart` with attributes specifically designed
   * to track a link click action in the context of referral bonus.
   *
   * @returns {void} No return value
   */
  const trackInviteLinkClickEvent = useCallback((): void => {
    if (isMenuInviteActive) {
      return;
    }

    analyticsTrackReferralBonusStart({
      [ANALYTICS_EVENT_ATTRIBUTES.source]: ANALYTICS_EVENT_SOURCE.account,
    });
  }, [isMenuInviteActive]);

  const renderAccountHero = (): JSX.Element => {
    return (
      <>
        {isLoggedInContentAvailable && (
          <AccountHero
            user={{
              id: userInfoSWR?.id,
              firstName: userInfoSWR?.firstName,
              lastName: userInfoSWR?.lastName,
              createdAt: userInfoSWR?.createdAt,
              lifetimePoints: userPointsFromACTSWR?.lifetimePoints,
              pointsToCurrencyRatio: userPointsFromACTSWR?.pointsToCurrencyRatio
            }}
          />
        )}
      </>
    );
  };

  /**
   * Render user's completed surveys, lifetime points and membership duration
   */
  const renderUserData = (): JSX.Element | undefined => {
    //disabled until the rest of the account items is done SJACTIV-437
    if (IS_SETTINGS_DISABLED) {
      return;
    }

    if (!isLoggedInContentAvailable) {
      return;
    }

    return(
      <UserDataContainer>
        <UserData>
          <DataPoint>
            124
            <span>{t('AccountSidebar--userAvatar-data-surveys')}</span>
          </DataPoint>
          <Divider/>
          <DataPoint>
            {userLifetimePoints}
            <span>{t('AccountSidebar--userAvatar-data-totalPoints')}</span>
          </DataPoint>
          <Divider/>
          <DataPoint>
            {membershipDuration.toString()}
            <span>{membershipLabel}</span>
          </DataPoint>
        </UserData>
      </UserDataContainer>
    );
  };

  /**
   * Renders the help section
   * @returns JSX.Element
   */
  const renderSettingsNavigation = (): JSX.Element | undefined => {
    if (!isLoggedInContentAvailable) {
      return;
    }

    return <MenuContainer>
      <MenuTitle>{ t('AccountSidebar--menuHeader-settings') }</MenuTitle>
      <MenuLink isActive={ isMenuPersonalInfoActive } text={ t('AccountSidebar--menuItem-personalInfo') } url={ MENU_ROUTES.ACCOUNT_PERSONAL_INFO } />
      {userInfo?.inviteId && (
        <MenuLink isActive={isMenuInviteActive} text={t('AccountSidebar--inviteFriends')} url={MENU_ROUTES.ACCOUNT_INVITE} onClick={trackInviteLinkClickEvent} />
      )}
      {gpCompleted && (
        <MenuLink isActive={isMenuProfileQuestionsActive} text={t('AccountSidebar--profileQuestions')} url={MENU_ROUTES.ACCOUNT_PROFILE_QUESTIONS} onClick={trackInviteLinkClickEvent} />
      )}
    </MenuContainer>;
  };

  /**
   * Renders the social section
   * @returns JSX.Element | undefined
   */
  const renderSocialNavigation = (): JSX.Element | undefined => {
    if (!isLoggedInContentAvailable || !isLeaderBoardAvailable) {
      return;
    }

    return <MenuContainer>
      <MenuTitle>{ t('AccountSidebar--menuHeader-social') }</MenuTitle>
      <MenuLink isActive={ isMenuLeaderboardActive } text={ t('AccountSidebar--menuItem-leaderboard') } url={ MENU_ROUTES.ACCOUNT_LEADERBOARD } />
    </MenuContainer>;
  };


  /**
   * Renders the help section
   * @returns JSX.Element
   */
  const renderHelpNavigation = (): JSX.Element => {
    return <MenuContainer>
      <MenuTitle>{ t('AccountSidebar--menuHeader-help') }</MenuTitle>
      <MenuLink isActive={ isMenuGetSupportActive } text={ t('AccountSidebar--menuItem-getSupport') } url={ MENU_ROUTES.ACCOUNT_SUPPORT } />
      <MenuLink isActive={ isMenuContactUsActive } text={ t('AccountSidebar--menuItem-contactUs') } url={ MENU_ROUTES.ACCOUNT_CONTACT_US } />
    </MenuContainer>;
  };

  /**
   * Renders the legal section
   * @returns JSX.Element
   */
  const renderLegalNavigation = (): JSX.Element => {
    return <MenuContainer>
      <MenuTitle>{ t('AccountSidebar--menuHeader-legal') }</MenuTitle>
      <MenuLink isActive={ isMenuTermsActive } text={ t('AccountSidebar--menuItem-terms') } url={ MENU_ROUTES.ACCOUNT_TERMS } />
      <MenuLink isActive={ isMenuPrivacyActive } text={ t('AccountSidebar--menuItem-privacy') } url={ MENU_ROUTES.ACCOUNT_PRIVACY } />
    </MenuContainer>;
  };

  /**
   * Renders the GDPR link
   * @returns JSX.Element | undefined
   */
  const renderGDPRLink = (): JSX.Element => {
    return <GDPRLink href={ process.env.REACT_APP_ONE_TRUST_DO_NOT_SELL_URL } target="_blank">
      { isLoggedOut ? t('AccountSidebar--exerciseRights') : t('AccountSidebar--doNotSell') }
    </GDPRLink>;
  };

  /**
   * Renders the log out link if user is logged in
   * @returns JSX.Element | undefined
   */
  const renderLogOut = (): JSX.Element | undefined => {
    if (isLoggedOut) {
      return;
    }

    return <LogOutButton>
      <Button
        context  = { ContextTypes.NEGATIVE_SECONDARY }
        onClick  = { () => logUserOut(false, undefined, undefined, ANALYTICS_EVENT_SOURCE.user) }
        iconLeft = { ICON_IDS.SIGN_OUT }
        width    = { ButtonWidth.FULL }>
        { t('AccountSidebar--logOut') }
      </Button>
    </LogOutButton>;
  };

  /**
   * Renders the copyright text
   * @returns JSX.Element
   */
  const renderCopyright = (): JSX.Element => {
    return(
      <Copyright>
        {t('AccountSidebar--copyright', {symbol:'\u00A9', year: currentYear})}
      </Copyright>
    );
  };

  /**
   * Returns the logo and the title in logout state
   * @returns
   */
  const renderLogo = (): JSX.Element | undefined => {
    if (!isLoggedOut) {
      return;
    }

    return(
      <LogoContainer href={process.env.REACT_APP_MONOLITH_BASE_URL} data-testid={LOGO_TEST_ID}>
        <LogoImage src={Logo} alt="logo" />
        <LogoText>{t('AccountSidebar--logo-title')}</LogoText>
      </LogoContainer>
    );
  };

  return (
    <SidebarContainer data-testid={ACCOUNT_SIDEBAR_TEST_ID}>
      { renderLogo() }
      { renderAccountHero() }
      { renderUserData() }
      { renderSettingsNavigation() }
      { renderSocialNavigation() }
      { renderHelpNavigation() }
      { renderLegalNavigation() }
      { renderGDPRLink() }
      { renderLogOut() }
      { renderCopyright() }
    </SidebarContainer>
  );
}

export default AccountSidebar;
